import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import { submitOrderFeedback } from 'api/orderApi';
import { store } from 'react-notifications-component';
import "./SubmitFeedbackToVendorModal.scss";
import { useHistory } from 'react-router-dom';
import { showSignInModal } from 'redux/actions/modalActions';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

const MAX_STARS = 5;

const SubmitFeedbackToVendorModal = ({ show, onClose, className, orderId }) => {
  const history = useHistory();
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0); 
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const userSignedIn = useSelector(state => get(state, 'user.user.confirmed', false));

  useEffect(() => {
    if (!userSignedIn) {
      dispatch(showSignInModal());
    }
  }, []);


  const handleClickStar = (index, e) => {
    const { left, width } = e.target.getBoundingClientRect();
    const percent = (e.clientX - left) / width;
    const value = index + percent;
    setRating(Math.min(value, MAX_STARS));
  };

  // For hover effect
  const handleMouseMoveStar = (index, e) => {
    const { left, width } = e.target.getBoundingClientRect();
    const percent = (e.clientX - left) / width;
    const value = index + percent;
    setHoverRating(Math.min(value, MAX_STARS));
  };

  const handleMouseLeave = () => {
    setHoverRating(0);
  };

  const handleSubmit = async () => {    
    if (rating === 0) return;
    setIsSubmitting(true);

    if (!userSignedIn) {
      store.addNotification({
        title: 'Login Required',
        message: 'You need to login to submit order feedback.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
      return;
    }

    try {
      const response = await submitOrderFeedback(orderId, {
        rating: parseFloat(rating.toFixed(1)),
        comment: comment,
      });

      store.addNotification({
        title: 'Success!',
        message: response?.message,
        type: 'success',
        insert: 'top',
        container: 'top-right',
        dismiss: {
          duration: 3000,
          onScreen: true
        }
      });
      onClose();
      setComment('');
      setRating(0);
      history.push('/browse-restaurants');
    } catch (error) {
      store.addNotification({
        title: 'Error',
        message: error.response?.data?.message,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="feedback-modal"
      className={className}
    >
      <button className="close" type="button" aria-label="Close" onClick={onClose} />

      <Modal.Body>
        <h2>Submit Feedback</h2>
        <p>Share your thoughts about your order:</p>

        <textarea
          rows="3"
          className="feedback-textarea"
          placeholder="e.g. Food was fresh and delicious! Delivery was on time."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <div className="rating">
          <label>Rate your experience:</label>

          <div className="stars" onMouseLeave={handleMouseLeave}>
            {[...Array(MAX_STARS)].map((_, index) => {
              const starValue = index + 1;
              let starClass = "star";

              if (displayRating >= starValue) starClass += " full";
              else if (displayRating >= starValue - 0.5) starClass += " half";

              return (
                <span
                  key={index}
                  className={starClass}
                  onClick={(e) => handleClickStar(index, e)}
                  onMouseMove={(e) => handleMouseMoveStar(index, e)}
                >
                  ★
                </span>
              );
            })}
          </div>
        </div>

        <div className="actions">
          <button
            className="btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send Feedback"}
          </button>

          <button
            className="btn-ghost-muted"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

SubmitFeedbackToVendorModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
  orderId: PropTypes.string.isRequired,
};

SubmitFeedbackToVendorModal.defaultProps = {
  show: false,
  onClose: () => { },
  className: '',
};

export default SubmitFeedbackToVendorModal;
